import { isNil } from 'lodash-es';
import { Trans, useLingui } from '@lingui/react';
import { type ComponentProps, type ReactNode, useMemo } from 'react';
import { Stack } from '@startuptools/ui/Stack';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { Typography } from '@startuptools/ui/Typography';
import { Chip } from '@startuptools/ui/Chip';
import EmailIcon from '@mui/icons-material/Email';
import { GqlEmailStatus, GqlJobState } from '../../graphql/base-types.graphql';
import { emailStatus } from './email-status';

type Color = ComponentProps<typeof EmailIcon>['color'];

const statuses: Record<GqlEmailStatus, { text: ReactNode; color: Color }> = {
  [GqlEmailStatus.Processed]: {
    text: <Trans id="Processed" />,
    color: 'warning',
  },
  [GqlEmailStatus.Dropped]: {
    text: <Trans id="Dropped" />,
    color: 'error',
  },
  [GqlEmailStatus.Delivered]: {
    text: <Trans id="Delivered" />,
    color: 'success',
  },
  [GqlEmailStatus.Deferred]: {
    text: <Trans id="Try again" />,
    color: 'error',
  },
  [GqlEmailStatus.Bounce]: { text: <Trans id="Bounced" />, color: 'error' },
  [GqlEmailStatus.Blocked]: {
    text: <Trans id="Blocked" />,
    color: 'error',
  },
  [GqlEmailStatus.Bounced]: {
    text: <Trans id="Bounced" />,
    color: 'error',
  },
  [GqlEmailStatus.Rejected]: {
    text: <Trans id="Rejected" />,
    color: 'error',
  },
  [GqlEmailStatus.Sent]: {
    text: <Trans id="Delivered" />,
    color: 'success',
  },
  [GqlEmailStatus.SoftBounced]: {
    text: <Trans id="Bounced" />,
    color: 'error',
  },
  [GqlEmailStatus.Spam]: {
    text: <Trans id="Marked as spam" />,
    color: 'error',
  },
  [GqlEmailStatus.Queued]: {
    text: <Trans id="Sending..." />,
    color: 'warning',
  },
  [GqlEmailStatus.Unsub]: { text: <Trans id="Sent" />, color: 'success' },
};

const jobStates: Record<GqlJobState, { text: ReactNode; color: Color }> = {
  [GqlJobState.Scheduled]: { text: <Trans id="Sending..." />, color: 'warning' },
  [GqlJobState.Waiting]: { text: <Trans id="Sending..." />, color: 'warning' },
  [GqlJobState.InProgress]: { text: <Trans id="Sending..." />, color: 'warning' },
  [GqlJobState.Failed]: {
    text: <Trans id="Failed, contact support" />,
    color: 'error',
  },
  [GqlJobState.Completed]: { text: <Trans id="Sending..." />, color: 'warning' },
};

export const EmailStatus = ({
  emailState,
  failReason,
  currentEmail,
  summonsEmail,
  emailSummonsId,
  summonsDocId,
  jobState,
}: {
  emailState: GqlEmailStatus | null | undefined;
  failReason: string | null | undefined;
  currentEmail: string | null | undefined;
  summonsEmail: string | null | undefined;
  emailSummonsId: string | null | undefined;
  jobState: GqlJobState | null | undefined;
  summonsDocId: string | null | undefined;
}) => {
  const { i18n } = useLingui();

  const status = useMemo((): {
    status: { text: ReactNode | null | undefined; color: Color } | null;
    ok: boolean;
    emailHasChanged: boolean;
    oldSummons: boolean;
  } => {
    const status = emailStatus({ emailState, currentEmail, summonsEmail, emailSummonsId, summonsDocId, jobState });
    if (!isNil(emailState) && (isNil(jobState) || jobState === GqlJobState.Completed)) {
      return {
        ...status,
        status: statuses[emailState],
      };
    } else if (!isNil(jobState)) {
      return {
        ...status,
        status: jobStates[jobState],
      };
    }
    return { ...status, status: null };
  }, [emailState, jobState, currentEmail, summonsEmail, summonsDocId, emailSummonsId]);

  return (
    <>
      {status.status && (
        <Stack direction="row" gap={1} flexWrap="nowrap">
          <Tooltip
            title={i18n._({
              id: 'Failed with reason (from our email provider): "{failReason}"',
              values: { failReason },
            })}
            disableHoverListener={!failReason}
          >
            <Typography startDecorator={<EmailIcon color={status.status?.color} />}>{status.status?.text} </Typography>
          </Tooltip>
          {status.emailHasChanged && (
            <Tooltip title={<Trans id="Email was previously sent to {email}" values={{ email: summonsEmail }} />}>
              <Chip size="sm" color="warning">
                <Trans id="Email changed" />
              </Chip>
            </Tooltip>
          )}
          {status.oldSummons && (
            <Chip size="sm" color="warning">
              <Trans id="Old summons" />
            </Chip>
          )}
        </Stack>
      )}
    </>
  );
};
